// src/pages/AcceptCohortInvite.jsx
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Building2,
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
  LogOut,
  Mail,
  Send,
  ShieldCheck,
  Users,
  XCircle,
} from "lucide-react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { getOrCreateUserProfile } from "../../lib/account/profile";
import { getOnboardingCompletionState } from "../../lib/onboarding/profileGate";
import {
  acceptPartnerInvitation,
  getPartnerInviteContext,
} from "../../lib/partner/partnerInvitations";

function cleanAuthUrl(token) {
  const cleanUrl =
    `/accept-cohort-invite?token=${encodeURIComponent(
      token
    )}`;

  window.history.replaceState(
    {},
    document.title,
    cleanUrl
  );
}

async function establishSessionFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");

  // Supabase-js may already have processed the code through
  // detectSessionInUrl, so only exchange if no session exists.
  const { data: existingData, error: existingError } = await supabase.auth.getSession();
  if (existingError) throw existingError;
  if (existingData?.session?.user) return existingData.session;

  if (code) {
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) throw error;
    return data?.session || null;
  }

  // Modern Supabase-js v2 uses setSession, not getSessionFromUrl.
  const hash = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
  const accessToken = hash.get("access_token");
  const refreshToken = hash.get("refresh_token");
  if (accessToken && refreshToken) {
    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) throw error;
    return data?.session || null;
  }

  return null;
}

function getPasswordIssues(
  password = ""
) {
  const issues = [];

  if (password.length < 8) {
    issues.push(
      "at least 8 characters"
    );
  }

  if (
    !/[a-z]/.test(password)
  ) {
    issues.push(
      "a lowercase letter"
    );
  }

  if (
    !/[A-Z]/.test(password)
  ) {
    issues.push(
      "an uppercase letter"
    );
  }

  if (
    !/[0-9]/.test(password)
  ) {
    issues.push("a number");
  }

  if (
    !/[^A-Za-z0-9]/.test(
      password
    )
  ) {
    issues.push(
      "a special character"
    );
  }

  return issues;
}

// Basic details are part of registration, not the business onboarding.
// Keep the format consistent with the normal Register page.
function normalizeSouthAfricanMobile(value) {
  const cleaned = String(value || "").replace(/[\s().-]/g, "");
  let digits = cleaned;
  if (digits.startsWith("+27")) digits = `0${digits.slice(3)}`;
  else if (digits.startsWith("27")) digits = `0${digits.slice(2)}`;
  if (!/^0[6-8]\d{8}$/.test(digits)) return null;
  return `+27${digits.slice(1)}`;
}

function getCohortCallbackUrl(token) {
  return `${window.location.origin}/auth/callback?cohortInviteToken=${encodeURIComponent(token)}`;
}

export default function AcceptCohortInvite() {
  const location =
    useLocation();

  const navigate =
    useNavigate();

  const ran =
    useRef(false);

  const redirectTimer =
    useRef(null);

  const token =
    useMemo(() => {
      const params =
        new URLSearchParams(
          location.search
        );

      const urlToken =
        params.get("token");

      if (urlToken) {
        sessionStorage.setItem(
          "pendingPartnerCohortInviteToken",
          urlToken
        );

        return urlToken;
      }

      return (
        sessionStorage.getItem(
          "pendingPartnerCohortInviteToken"
        ) || ""
      );
    }, [location.search]);

  const [context, setContext] =
    useState(null);

  const [phase, setPhase] =
    useState("loading");

  const [error, setError] =
    useState("");

  const [email, setEmail] =
    useState("");

  const [authMode, setAuthMode] = useState("register");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [registering, setRegistering] = useState(false);
  const [requiresNewPassword, setRequiresNewPassword] = useState(false);

  const [
    signedInEmail,
    setSignedInEmail,
  ] = useState("");

  const [
    currentUser,
    setCurrentUser,
  ] = useState(null);

  const [
    sendingLoginLink,
    setSendingLoginLink,
  ] = useState(false);

  const [
    loginLinkSent,
    setLoginLinkSent,
  ] = useState(false);

  const [
    password,
    setPassword,
  ] = useState("");

  const [
    confirmPassword,
    setConfirmPassword,
  ] = useState("");

  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  const [
    showConfirmPassword,
    setShowConfirmPassword,
  ] = useState(false);

  const [
    savingPassword,
    setSavingPassword,
  ] = useState(false);

  const [
    destination,
    setDestination,
  ] = useState(
    "/profile-gate"
  );

  function scheduleRedirect(
    nextDestination
  ) {
    if (
      redirectTimer.current
    ) {
      window.clearTimeout(
        redirectTimer.current
      );
    }

    redirectTimer.current =
      window.setTimeout(() => {
        navigate(
          nextDestination,
          {
            replace: true,
          }
        );
      }, 2200);
  }

  async function finishCohortAcceptance(user) {
    setPhase("accepting");
    const result = await acceptPartnerInvitation(token);

    let onboardingCompleted = false;
    try {
      const onboardingState = await getOnboardingCompletionState();
      onboardingCompleted = onboardingState.onboardingCompleted === true;
    } catch (onboardingError) {
      console.warn("Could not confirm onboarding completion:", onboardingError);
    }

    const nextDestination = onboardingCompleted ? "/dashboard" : "/profile-gate";
    setDestination(nextDestination);
    setContext((current) => ({
      ...(current || {}),
      partner_name: result.partner_name || current?.partner_name,
      cohort_name: result.cohort_name || current?.cohort_name,
    }));
    setCurrentUser(user);
    sessionStorage.removeItem("pendingPartnerCohortInviteToken");
    setPhase("success");
    scheduleRedirect(nextDestination);
  }

  useEffect(() => {
    if (ran.current) return;

    ran.current = true;

    async function processInvitation() {
      try {
        setPhase("loading");
        setError("");

        if (!token) {
          throw new Error(
            "The cohort invitation token is missing."
          );
        }

        const inviteContext =
          await getPartnerInviteContext(
            token
          );

        setContext(
          inviteContext
        );

        let session = null;

        try {
          session =
            await establishSessionFromUrl();

          if (session) {
            cleanAuthUrl(token);
          }
        } catch (sessionError) {
          console.warn(
            "Invitation session could not be established:",
            sessionError
          );
        }

        if (
          inviteContext.status ===
            "revoked" ||
          inviteContext.status ===
            "expired"
        ) {
          setPhase(
            "unavailable"
          );

          return;
        }

        if (
          !inviteContext.can_accept &&
          inviteContext.status !==
            "accepted"
        ) {
          setPhase(
            "unavailable"
          );

          return;
        }

        if (!session?.user) {
          if (
            inviteContext.status ===
            "accepted"
          ) {
            setPhase(
              "already-accepted"
            );
          } else {
            setPhase(
              "authentication-required"
            );
          }

          return;
        }

        setCurrentUser(session.user);
        setSignedInEmail(session.user.email || "");

        // An invitation created through inviteUserByEmail already owns an Auth
        // identity. It must finish the real Gwin registration form BEFORE
        // accepting cohort membership.
        const requiresPassword =
          session.user.user_metadata?.partner_cohort_requires_password === true;

        if (requiresPassword) {
          // Only Auth accounts created by Gwin's cohort invitation flow
          // require the special registration-completion form here.
          // Existing accounts keep their own profile and proceed through
          // normal authenticated acceptance / unfinished Profile Gate.
          setFullName(session.user.user_metadata?.full_name || "");
          setPhone(session.user.user_metadata?.mobile_number || "");
          setRequiresNewPassword(true);
          setPhase("registration-required");
          return;
        }

        await finishCohortAcceptance(session.user);
      } catch (processError) {
        console.error(
          "Accepting cohort invitation failed:",
          processError
        );

        setError(
          processError?.message ||
            "The invitation could not be accepted."
        );

        setPhase("error");
      }
    }

    processInvitation();

    return () => {
      if (
        redirectTimer.current
      ) {
        window.clearTimeout(
          redirectTimer.current
        );
      }
    };

    // The invitation should only run once
    // for the current page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleCreateAccount(event) {
    event.preventDefault();
    setError("");
    setRegistering(true);

    try {
      if (!context?.can_accept || context?.status !== "pending") {
        throw new Error("This invitation is no longer available for registration.");
      }
      const cleanEmail = String(email || "").trim().toLowerCase();
      const cleanName = String(fullName || "").trim().replace(/\s+/g, " ");
      const cleanPhone = normalizeSouthAfricanMobile(phone);
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
        throw new Error("Please enter the email address that received this invitation.");
      }
      if (!cleanName) throw new Error("Please enter your full name.");
      if (!cleanPhone) throw new Error("Enter a valid South African mobile number.");
      const issues = getPasswordIssues(password);
      if (issues.length) {
        throw new Error(`Your password still needs ${issues.join(", ")}.`);
      }
      if (password !== confirmPassword) {
        throw new Error("The passwords do not match.");
      }

      // This only covers a learner whose invitation exists but the auth user
      // was never created. If inviteUserByEmail already created the account,
      // they should open the invitation email to authenticate and finish setup.
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: cleanEmail,
        password,
        options: {
          emailRedirectTo: getCohortCallbackUrl(token),
          data: {
            full_name: cleanName,
            mobile_number: cleanPhone,
            phone: cleanPhone,
          },
        },
      });
      if (signUpError) throw signUpError;

      // Supabase may deliberately obscure whether an existing account exists.
      // Never treat a missing identity as successful new registration.
      if (Array.isArray(data?.user?.identities) && data.user.identities.length === 0) {
        throw new Error(
          "An account may already exist for this email. Use 'Already registered' or open your newest cohort invitation email."
        );
      }

      if (data?.session?.user) {
        // Occurs when email confirmation is disabled. Do not leave the app on
        // an old effect with a now-signed-in session.
        window.location.assign(`/accept-cohort-invite?token=${encodeURIComponent(token)}`);
        return;
      }

      setPhase("verification-required");
    } catch (registerError) {
      console.error("Invited learner registration failed:", registerError);
      setError(registerError?.message || "Could not create your Gwin account.");
    } finally {
      setRegistering(false);
    }
  }

  async function handleSendLoginLink(
    event
  ) {
    event.preventDefault();

    try {
      setSendingLoginLink(true);
      setLoginLinkSent(false);
      setError("");

      const cleanEmail =
        String(email || "")
          .trim()
          .toLowerCase();

      if (
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          cleanEmail
        )
      ) {
        throw new Error(
          "Please enter the email address that received the invitation."
        );
      }

      const redirectTo =
        `${window.location.origin}` +
        `/accept-cohort-invite` +
        `?token=${encodeURIComponent(
          token
        )}`;

      const {
        error: otpError,
      } =
        await supabase.auth
          .signInWithOtp({
            email: cleanEmail,

            options: {
              shouldCreateUser:
                false,

              emailRedirectTo:
                redirectTo,
            },
          });

      if (otpError) {
        throw otpError;
      }

      setLoginLinkSent(true);
    } catch (sendError) {
      console.error(
        "Sending invitation login link failed:",
        sendError
      );

      const message = String(sendError?.message || "");
      setError(
        /signups not allowed for otp/i.test(message)
          ? "No existing Gwin account was found. Select Create account instead, or use the original invitation email to complete registration."
          : message || "The sign-in link could not be sent."
      );
    } finally {
      setSendingLoginLink(
        false
      );
    }
  }

  async function handleCreatePassword(event) {
    event.preventDefault();
    setSavingPassword(true);
    setError("");

    try {
      if (!currentUser?.id) throw new Error("Please open your invitation again to sign in.");
      const cleanName = String(fullName || "").trim().replace(/\s+/g, " ");
      const cleanPhone = normalizeSouthAfricanMobile(phone);
      if (!cleanName) throw new Error("Please enter your full name.");
      if (!cleanPhone) throw new Error("Enter a valid South African mobile number.");

      if (requiresNewPassword || password || confirmPassword) {
        const issues = getPasswordIssues(password);
        if (issues.length) {
          throw new Error(`Your password still needs ${issues.join(", ")}.`);
        }
        if (password !== confirmPassword) {
          throw new Error("The passwords do not match.");
        }
      }

      const existingMetadata = currentUser.user_metadata || {};
      const updates = {
        data: {
          ...existingMetadata,
          full_name: cleanName,
          mobile_number: cleanPhone,
          phone: cleanPhone,
          partner_cohort_requires_password: false,
        },
      };
      if (password) updates.password = password;

      const { data, error: updateError } = await supabase.auth.updateUser(updates);
      if (updateError) throw updateError;
      const updatedUser = data?.user || currentUser;

      // Keep public.user aligned with Auth metadata so Profile Gate and
      // the existing Profile page can read the details the learner entered.
      await getOrCreateUserProfile();
      const { error: profileError } = await supabase
        .from("user")
        .update({ full_name: cleanName, mobile_number: cleanPhone })
        .eq("id", updatedUser.id);
      if (profileError) throw profileError;

      setCurrentUser(updatedUser);
      await finishCohortAcceptance(updatedUser);
    } catch (registrationError) {
      console.error("Completing invited learner registration failed:", registrationError);
      setError(registrationError?.message || "Could not complete registration.");
    } finally {
      setSavingPassword(false);
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut();

    setCurrentUser(null);
    setSignedInEmail("");
    setError("");
    setPhase(
      "authentication-required"
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF7ED] px-5 py-10 text-[#333333] sm:px-6">
      <div className="mx-auto w-full max-w-xl">
        <div className="mb-6 flex items-center justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-[#FF914D] text-white shadow-sm">
            <Building2 className="h-7 w-7" />
          </div>
        </div>

        <div className="overflow-hidden rounded-[30px] border border-[#FFE1CF] bg-white shadow-xl">
          <header className="border-b border-[#EFEFEF] px-6 py-6 text-center sm:px-8">
            <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF914D]">
              Gwin cohort invitation
            </p>

            <h1 className="mt-2 text-3xl font-black tracking-[-0.03em]">
              Join your learning cohort
            </h1>

            {context && (
              <div className="mt-5 rounded-2xl bg-[#FFF7ED] p-4 text-left">
                <InfoRow
                  icon={Building2}
                  label="Partner"
                  value={
                    context.partner_name
                  }
                />

                <InfoRow
                  icon={Users}
                  label="Cohort"
                  value={
                    context.cohort_name
                  }
                />

                <InfoRow
                  icon={Mail}
                  label="Invited email"
                  value={
                    context.email_masked
                  }
                />
              </div>
            )}
          </header>

          <main className="px-6 py-7 sm:px-8">
            {(phase === "loading" ||
              phase ===
                "accepting") && (
              <StatusBlock
                icon={Loader2}
                spinning
                title={
                  phase ===
                  "accepting"
                    ? "Adding you to the cohort"
                    : "Checking your invitation"
                }
                description="Please keep this page open while Gwin securely verifies the invitation."
              />
            )}

            {phase === "authentication-required" && (
              <>
                <StatusBlock
                  icon={ShieldCheck}
                  title="Join your Gwin cohort"
                  description="New to Gwin? Complete your basic registration. Already have an account? Sign in with the email that received this invitation."
                />

                <div className="mt-6 grid grid-cols-2 rounded-2xl bg-[#F6F6F6] p-1">
                  <button
                    type="button"
                    onClick={() => { setAuthMode("register"); setError(""); }}
                    className={`rounded-xl px-3 py-3 text-sm font-black ${authMode === "register" ? "bg-white text-[#FF914D] shadow" : "text-[#757575]"}`}
                  >
                    Create account
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAuthMode("login"); setError(""); }}
                    className={`rounded-xl px-3 py-3 text-sm font-black ${authMode === "login" ? "bg-white text-[#FF914D] shadow" : "text-[#757575]"}`}
                  >
                    Already registered
                  </button>
                </div>

                {error && <ErrorMessage message={error} />}

                {authMode === "register" ? (
                  <form onSubmit={handleCreateAccount} className="mt-6 space-y-4">
                    <p className="text-xs text-[#757575]">
                      Use the exact email address that received this cohort invitation.
                      You will verify ownership through your email before the cohort is added.
                    </p>
                    <label className="block text-sm font-black" htmlFor="new-invite-name">Full name</label>
                    <input
                      id="new-invite-name" type="text" autoComplete="name" required
                      value={fullName} onChange={(event) => setFullName(event.target.value)}
                      className="w-full rounded-2xl border border-[#DDDDDD] px-4 py-3 text-sm focus:border-[#FF914D]"
                      disabled={registering}
                    />
                    <label className="block text-sm font-black" htmlFor="new-invite-email">Invited email address</label>
                    <input
                      id="new-invite-email" type="email" autoComplete="email" required
                      value={email} onChange={(event) => setEmail(event.target.value)}
                      className="w-full rounded-2xl border border-[#DDDDDD] px-4 py-3 text-sm focus:border-[#FF914D]"
                      disabled={registering}
                    />
                    <label className="block text-sm font-black" htmlFor="new-invite-phone">Phone number</label>
                    <input
                      id="new-invite-phone" type="tel" autoComplete="tel" required
                      value={phone} onChange={(event) => setPhone(event.target.value)}
                      placeholder="0712345678"
                      className="w-full rounded-2xl border border-[#DDDDDD] px-4 py-3 text-sm focus:border-[#FF914D]"
                      disabled={registering}
                    />
                    <label className="block text-sm font-black" htmlFor="new-invite-password">Password</label>
                    <input
                      id="new-invite-password" type="password" autoComplete="new-password" required
                      value={password} onChange={(event) => setPassword(event.target.value)}
                      className="w-full rounded-2xl border border-[#DDDDDD] px-4 py-3 text-sm focus:border-[#FF914D]"
                      disabled={registering}
                    />
                    <label className="block text-sm font-black" htmlFor="new-invite-confirm">Confirm password</label>
                    <input
                      id="new-invite-confirm" type="password" autoComplete="new-password" required
                      value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)}
                      className="w-full rounded-2xl border border-[#DDDDDD] px-4 py-3 text-sm focus:border-[#FF914D]"
                      disabled={registering}
                    />
                    <p className="text-xs text-[#757575]">
                      At least 8 characters, including uppercase, lowercase, number and special character.
                    </p>
                    <button
                      type="submit" disabled={registering}
                      className="mt-2 w-full rounded-full bg-[#FF914D] px-6 py-3 text-sm font-black text-white disabled:opacity-60"
                    >
                      {registering ? "Registering…" : "Create Gwin account"}
                    </button>
                    <p className="text-xs leading-relaxed text-[#757575]">
                      Already received a Gwin account invitation email? Open its verification link first.
                      It may have already created your Auth account.
                    </p>
                  </form>
                ) : loginLinkSent ? (
                  <div className="mt-6 rounded-2xl border border-green-200 bg-green-50 p-5 text-center">
                    <Mail className="mx-auto h-7 w-7 text-green-700" />
                    <h2 className="mt-3 font-black text-green-900">Check your email</h2>
                    <p className="mt-2 text-sm leading-relaxed text-green-800">
                      Open the newest Gwin sign-in email to continue accepting your cohort invitation.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSendLoginLink} className="mt-6 space-y-4">
                    <label htmlFor="cohort-invite-login-email" className="block text-sm font-black">
                      Invited email address
                    </label>
                    <input
                      id="cohort-invite-login-email" type="email" required
                      autoComplete="email" value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      disabled={sendingLoginLink}
                      className="w-full rounded-2xl border border-[#DDDDDD] px-4 py-3 text-sm focus:border-[#FF914D]"
                    />
                    <button
                      type="submit" disabled={sendingLoginLink}
                      className="w-full rounded-full bg-[#FF914D] px-6 py-3 text-sm font-black text-white disabled:opacity-60"
                    >
                      <Send className="mr-2 inline h-4 w-4" />
                      {sendingLoginLink ? "Sending…" : "Email me a secure sign-in link"}
                    </button>
                  </form>
                )}
              </>
            )}

            {phase === "verification-required" && (
              <StatusBlock
                icon={Mail}
                title="Verify your email"
                description="We have sent a verification email if this is a new account. Open the latest Gwin email, verify your address, and return to the cohort invitation. If you already have an account, use the sign-in option instead."
              />
            )}

            {phase === "registration-required" && (
              <>
                <StatusBlock
                  icon={LockKeyhole}
                  title="Complete your Gwin registration"
                  description="Your email is verified. Add your basic details before joining your cohort and beginning onboarding."
                />
                {error && <ErrorMessage message={error} />}
                <form onSubmit={handleCreatePassword} className="mt-6 space-y-4">
                  <label className="block text-sm font-black" htmlFor="invited-confirmed-email">Verified email</label>
                  <input
                    id="invited-confirmed-email" type="email" readOnly
                    value={currentUser?.email || ""}
                    className="w-full rounded-2xl border border-[#DDDDDD] bg-[#F8F8F8] px-4 py-3 text-sm text-[#757575]"
                  />
                  <label className="block text-sm font-black" htmlFor="invited-learner-name">Full name</label>
                  <input
                    id="invited-learner-name" type="text" autoComplete="name" required
                    value={fullName} onChange={(event) => setFullName(event.target.value)}
                    disabled={savingPassword}
                    className="w-full rounded-2xl border border-[#DDDDDD] px-4 py-3 text-sm"
                  />
                  <label className="block text-sm font-black" htmlFor="invited-learner-phone">Phone number</label>
                  <input
                    id="invited-learner-phone" type="tel" autoComplete="tel" required
                    value={phone} onChange={(event) => setPhone(event.target.value)}
                    placeholder="0712345678" disabled={savingPassword}
                    className="w-full rounded-2xl border border-[#DDDDDD] px-4 py-3 text-sm"
                  />
                  <label className="block text-sm font-black" htmlFor="invited-learner-password">
                    {requiresNewPassword ? "Create password" : "New password (optional)"}
                  </label>
                  <div className="relative">
                    <input
                      id="invited-learner-password" type={showPassword ? "text" : "password"}
                      autoComplete="new-password" required={requiresNewPassword}
                      value={password} onChange={(event) => setPassword(event.target.value)}
                      disabled={savingPassword}
                      className="w-full rounded-2xl border border-[#DDDDDD] px-4 py-3 pr-12 text-sm"
                    />
                    <button type="button" onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-4 top-3 text-[#757575]">
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <label className="block text-sm font-black" htmlFor="invited-learner-confirm">Confirm password</label>
                  <div className="relative">
                    <input
                      id="invited-learner-confirm" type={showConfirmPassword ? "text" : "password"}
                      autoComplete="new-password" required={requiresNewPassword}
                      value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)}
                      disabled={savingPassword}
                      className="w-full rounded-2xl border border-[#DDDDDD] px-4 py-3 pr-12 text-sm"
                    />
                    <button type="button" onClick={() => setShowConfirmPassword((v) => !v)}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      className="absolute right-4 top-3 text-[#757575]">
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs leading-relaxed text-[#757575]">
                    If setting a password: at least 8 characters, uppercase, lowercase, number and special character.
                  </p>
                  <button
                    type="submit" disabled={savingPassword}
                    className="w-full rounded-full bg-[#FF914D] px-6 py-3 text-sm font-black text-white disabled:opacity-60"
                  >
                    {savingPassword ? "Saving…" : "Complete registration and continue"}
                  </button>
                </form>
              </>
            )}

            {phase === "success" && (
              <>
                <StatusBlock
                  icon={CheckCircle2}
                  success
                  title="Invitation accepted"
                  description={`You have been added to ${
                    context?.cohort_name ||
                    "the cohort"
                  } under ${
                    context?.partner_name ||
                    "the partner organisation"
                  }.`}
                />

                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      destination,
                      {
                        replace: true,
                      }
                    )
                  }
                  className="mt-6 w-full rounded-full bg-[#FF914D] px-6 py-3 text-sm font-black text-white"
                >
                  Continue to Gwin
                </button>

                <p className="mt-3 text-center text-xs font-semibold text-[#757575]">
                  Redirecting
                  automatically…
                </p>
              </>
            )}

            {phase ===
              "already-accepted" && (
              <StatusBlock
                icon={CheckCircle2}
                success
                title="Invitation already accepted"
                description="This invitation has already been used. Sign in through the normal Gwin login page to continue."
              />
            )}

            {phase ===
              "unavailable" && (
              <StatusBlock
                icon={XCircle}
                danger
                title="Invitation unavailable"
                description={
                  context?.status ===
                  "expired"
                    ? "This invitation has expired. Ask the partner organisation to send a fresh invitation."
                    : context?.status ===
                      "revoked"
                    ? "This invitation has been revoked by the partner organisation."
                    : "This cohort is no longer accepting learners."
                }
              />
            )}

            {phase === "error" && (
              <>
                <StatusBlock
                  icon={XCircle}
                  danger
                  title="Invitation could not be accepted"
                  description={
                    error ||
                    "An unexpected error occurred."
                  }
                />

                {signedInEmail && (
                  <div className="mt-6 rounded-2xl border border-[#EFEFEF] p-4">
                    <p className="text-sm text-[#757575]">
                      Currently signed
                      in as:
                    </p>

                    <p className="mt-1 break-all font-black">
                      {signedInEmail}
                    </p>

                    <button
                      type="button"
                      onClick={
                        handleSignOut
                      }
                      className="mt-4 inline-flex items-center rounded-full border border-[#DDDDDD] px-4 py-2 text-sm font-black"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign out and retry
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
}) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-white text-[#FF914D]">
        <Icon className="h-4 w-4" />
      </div>

      <div>
        <p className="text-xs font-black uppercase tracking-wider text-[#757575]">
          {label}
        </p>

        <p className="mt-0.5 font-black">
          {value ||
            "Not available"}
        </p>
      </div>
    </div>
  );
}

function StatusBlock({
  icon: Icon,
  title,
  description,
  spinning = false,
  success = false,
  danger = false,
}) {
  return (
    <div className="text-center">
      <div
        className={[
          "mx-auto flex h-14 w-14 items-center justify-center rounded-2xl",
          success
            ? "bg-green-100 text-green-700"
            : danger
            ? "bg-red-100 text-red-700"
            : "bg-[#FFF4EC] text-[#FF914D]",
        ].join(" ")}
      >
        <Icon
          className={[
            "h-7 w-7",
            spinning
              ? "animate-spin"
              : "",
          ].join(" ")}
        />
      </div>

      <h2 className="mt-5 text-xl font-black">
        {title}
      </h2>

      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-[#757575]">
        {description}
      </p>
    </div>
  );
}

function ErrorMessage({
  message,
}) {
  return (
    <div
      role="alert"
      className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
    >
      {message}
    </div>
  );
}